import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  IconButton,
  Popover,
  Checkbox,
  FormControlLabel,
  Typography,
  Button as MUIButton,
} from "@mui/material";
import { PiSortDescendingLight, PiSortAscendingLight } from "react-icons/pi";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { FaLink, FaUsers, FaEdit, FaTrash, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { MdSettings } from "react-icons/md";
import authService from "../../../utils/authService";
import { getAdminCompanyListCached } from "../../../utils/adminCompanyCache";
import { fetchContentCreatorsWithCache } from "../../../utils/ccUtils";
import { fetchAllTracksForCourses } from "../../../utils/trackUtils";
import MultiSelect, { OptionItem } from "../../../components/MultiSelect";
import { 
  ColumnConfig, 
  getDefaultProjectColumns, 
  loadColumnVisibility, 
  updateColumnVisibility, 
  resetColumnVisibility,
  PAGE_KEYS 
} from "../../../utils/columnVisibilityUtils";
import { PROJECT_STATUS_COLORS } from "./projectConfigConstants";

interface Data {
  project_id: string;
  project_name: string;
  track_id: string;
  project_description: string;
  project_allowed_subjects: string[] | null;
  subject_ids: string[] | null;
  company_id: string;
  company_name?: string;
  industry: string;
  industry_name?: string;
  level: string;
  del_row?: boolean;
  status: string;
  project_duration_in_days?: number | null;
  score?: number | null;
  project_demo_video_id?: string | null;
  by?: string;
  batches?: string[];
  assigned_cc_ids: string[] | null;
  project_data?: any;
}

interface Track {
  track_id: string;
  track_name: string;
}

interface Company {
  company_id: string;
  company_name: string;
  del_row?: boolean;
}

interface Industry {
  industry_name: string;
}

interface Subject {
  subject_id: string;
  subject_name: string;
  is_project?: boolean;
}

const Project: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState<Data[]>([]);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [contentCreators, setContentCreators] = useState<{ user_id: string; user_name: string }[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  // Helper function to check if a project is deleted
  const isDeleted = (project: Data): boolean => {
    return project.del_row === true;
  };

  // Helper function to check if a company is deleted
  const isCompanyDeleted = (company: Company): boolean => {
    return company.del_row === true;
  };

  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string>("new");
  const [loading, setLoading] = useState<boolean>(false);

  const [formData, setFormData] = useState<Data>({
    project_id: "",
    project_name: "",
    track_id: "",
    project_description: "",
    project_allowed_subjects: [],
    subject_ids: [],
    company_id: "",
    industry: "",
    level: "",
    status: "",
    assigned_cc_ids: null,
  });
  const [error, setError] = useState<string>("");
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [selectedProject, setSelectedProject] = useState<Data>();
  const [search, setSearch] = useState("");
  const [selectedTrack, setSelectedTrack] = useState("");
  const [sortField, setSortField] = useState<keyof Data | "status" | "batch">("project_id");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [columns, setColumns] = useState<ColumnConfig[]>(() => 
    loadColumnVisibility(PAGE_KEYS.PROJECT, getDefaultProjectColumns())
  );
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(null);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [hasNext, setHasNext] = useState<boolean>(false);
  const [hasPrev, setHasPrev] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  useEffect(() => {
    fetchContentCreators();
  }, []);

  const handleModal = async (type: string, project?: Data): Promise<void> => {
    setError("");
    setModalType(type);
    if (type === "new") {
      setFormData({
        project_id: "",
        track_id: selectedTrack,
        project_name: "",
        project_description: "",
        project_allowed_subjects: [],
        subject_ids: [],
        company_id: "",
        industry: "",
        level: "",
        status: "",
        assigned_cc_ids: null,
      });
      setSelectedUserIds([]);
      setShowModal(true);
    } else if (project) {
      if (project.track_id) {
        await fetchSubjectsForTrack(project.track_id);
      }
      const editFormData = {
        ...project,
        project_allowed_subjects: Array.isArray(
          project.project_allowed_subjects || project.subject_ids
        )
          ? project.project_allowed_subjects || project.subject_ids
          : [],
        status: project.status,
      };
      setFormData(editFormData);
      setSelectedUserIds(project.assigned_cc_ids || []);
      setShowModal(true);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  /**
   * Extracts all subject_ids used in project_data
   * Recursively searches through phases, parts, tasks, and subtasks
   */
  const getSubjectsFromProjectData = (projectData: any): Set<string> => {
    const subjectIds = new Set<string>();
    
    if (!projectData || !projectData.content || !Array.isArray(projectData.content)) {
      return subjectIds;
    }
    
    projectData.content.forEach((phase: any) => {
      if (phase.parts && Array.isArray(phase.parts)) {
        phase.parts.forEach((part: any) => {
          if (part.tasks && Array.isArray(part.tasks)) {
            part.tasks.forEach((task: any) => {
              if (task.data && Array.isArray(task.data)) {
                task.data.forEach((subtask: any) => {
                  if (subtask.subject_id) {
                    subjectIds.add(subtask.subject_id);
                  }
                });
              }
            });
          }
        });
      }
    });
    
    return subjectIds;
  };

  const handleSubjectChange = (selectedSubjects: string[]): void => {
    // Get subjects used in project_data (if editing existing project)
    const subjectsInProjectData = formData.project_data 
      ? getSubjectsFromProjectData(formData.project_data)
      : new Set<string>();
    
    // Convert Set to array and merge with selected subjects - Set ensures uniqueness
    const requiredSubjectsArray = Array.from(subjectsInProjectData);
    const finalSelectedSet = new Set([...requiredSubjectsArray, ...selectedSubjects]);
    const finalSelected = Array.from(finalSelectedSet);
    
    setFormData((prevData) => ({
      ...prevData,
      project_allowed_subjects: finalSelected,
    }));
  };

  const handleTrackChange = (event: SelectChangeEvent<string>) => {
    const trackId = event.target.value as string;
    setSelectedTrack(trackId);
    // Store track_id in sessionStorage
    if (trackId) {
      sessionStorage.setItem("track", trackId);
    } else {
      sessionStorage.removeItem("track");
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field as keyof Data | "status" | "batch");
      setSortDirection("asc");
    }
  };

  const handleColumnVisibilityChange = (columnKey: string) => {
    const updatedColumns = updateColumnVisibility(PAGE_KEYS.PROJECT, columns, columnKey);
    setColumns(updatedColumns);
  };

  const handleSettingsClick = (event: React.MouseEvent<HTMLElement>) => {
    setSettingsAnchor(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchor(null);
  };

  const getSortedProjects = (): Data[] => {
    const filtered = projects
      .filter((project) => !isDeleted(project))
      .filter((project) =>
        project.project_name.toLowerCase().includes(search.toLowerCase())
      );

    if (!sortField) {
      return filtered;
    }

    return [...filtered].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      const field = sortField as keyof Data | "status" | "batch";
      switch (field) {
        case "project_id":
          aValue = Number(a.project_id ?? 0);
          bValue = Number(b.project_id ?? 0);
          break;

        case "project_name":
          aValue = a.project_name.toLowerCase();
          bValue = b.project_name.toLowerCase();
          break;
        case "project_description":
          aValue = a.project_description.toLowerCase();
          bValue = b.project_description.toLowerCase();
          break;
        case "company_name":
          aValue = (a.company_name || "").toLowerCase();
          bValue = (b.company_name || "").toLowerCase();
          break;
        case "industry":
          aValue = (a.industry || "").toLowerCase();
          bValue = (b.industry || "").toLowerCase();
          break;
        case "status":
          aValue = (a.status || "").toLowerCase();
          bValue = (b.status || "").toLowerCase();
          break;
        case "batch":
          aValue = a.batches?.length || 0;
          bValue = b.batches?.length || 0;
          break;
        default:
          return 0;
      }

      // 🔹 Numeric sort (project_id)
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      // 🔹 String sort
      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return 0;
    });
  };

  const sortedProjects = getSortedProjects();

  const handleSave = async (): Promise<void> => {
    setError("");
    const existingProject = projects.find(
      (project) =>
        project.project_name.toLowerCase() ===
          formData.project_name.toLowerCase() &&
        project.project_id?.toLowerCase() !== formData.project_id?.toLowerCase()
    );

    if (existingProject) {
      setError("Project Name not available");
      return;
    }
    if (formData.project_name == "") {
      setError("Please enter a project name");
      return;
    }
    if (formData.track_id == "") {
      setError("Please select a track");
      return;
    }
    if (formData.company_id == "") {
      setError("Please select a company");
      return;
    }
    if (formData.industry == "") {
      setError("Please select an industry");
      return;
    }
    if (formData.level == "") {
      setError("Please select a level");
      return;
    }
    if (
      !formData.project_allowed_subjects ||
      formData.project_allowed_subjects.length === 0
    ) {
      setError("Please select at least one subject");
      return;
    }
    // Check if at least one selected subject has is_project: true
    const selectedSubjectsWithProjectFlag = subjects.filter((subject) =>
      formData.project_allowed_subjects?.includes(subject.subject_id)
    );
    const hasProjectSubject = selectedSubjectsWithProjectFlag.some(
      (subject) => subject.is_project === true
    );
    if (!hasProjectSubject) {
      setError("Select atleast one Project subject");
      return;
    }
    const projectsToSend = {
      project_id: formData.project_id || "",
      track_id: formData.track_id,
      project_name: formData.project_name,
      project_description: formData.project_description || "",
      project_pic: null,
      project_allowed_subjects: formData.project_allowed_subjects,
      company_id: formData.company_id,
      industry: formData.industry,
      level: formData.level,
      project_data: null,
      project_duration_in_days: null,
      project_demo_video_id: null,
      by: sessionStorage.getItem("Email"),
      assigned_cc_ids: selectedUserIds,
    };
    try {
      setLoading(true);
      const response = await authService.post(
        "admin/create-project",
        projectsToSend
      );
      setLoading(false);
      setShowModal(false);
      
      // Get project_id from response (could be response.data.project_id or response.data.project.project_id)
      const savedProjectId = response.data?.project_id ||  formData.project_id;
      const savedProjectName = formData.project_name;
      
      // Navigate to project-configuration with project details
      navigate(`/admin/project-configuration`, {
        state: {
          track_id: formData.track_id,
          project_id: savedProjectId,
          project_name: savedProjectName,
        },
      });
    } catch (error) {
      setLoading(false);
      console.error("Error saving projects:", error);
    }
  };
  useEffect(() => {
    fetchTracks();
    fetchCompanies();
    // Check if track_id is passed from navigation state, otherwise get from sessionStorage
    if (location.state && (location.state as any).track_id) {
      const trackId = (location.state as any).track_id;
      setSelectedTrack(trackId);
      // Store in sessionStorage
      if (trackId) {
        sessionStorage.setItem("track", trackId);
      }
    } else {
      // Get from sessionStorage if not in location.state
      const savedTrack = sessionStorage.getItem("track");
      if (savedTrack) {
        setSelectedTrack(savedTrack);
      }
    }
  }, [location.state]);

  useEffect(() => {
    if (selectedTrack) {
      setPage(1); // Reset to first page when track changes
      fetchProjects(selectedTrack, 1, pageSize);
      fetchSubjectsForTrack(selectedTrack);
    } else {
      setProjects([]);
      setSubjects([]);
    }
  }, [selectedTrack]);

  useEffect(() => {
    if (formData.track_id) {
      fetchSubjectsForTrack(formData.track_id);
    } else {
      setSubjects([]);
    }
  }, [formData.track_id]);

  useEffect(() => {
    fetchIndustries();
  }, []);

  const fetchTracks = async () => {
    try {
      const data = await fetchAllTracksForCourses();
      setTracks(data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const { companies: list } = await getAdminCompanyListCached();
      setCompanies((list || []) as Company[]);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const fetchIndustries = async () => {
    try {
      const response = await authService.get(`admin/company/get-all-industry`);
      const industryNames = response.data.industry || [];
      const industryObjects = industryNames.map((name: string) => ({
        industry_name: name,
      }));
      setIndustries(industryObjects);
    } catch (error) {
      console.error("Error fetching industries:", error);
      setIndustries([]);
    }
  };

  const fetchSubjectsForTrack = async (trackId: string) => {
    try {
      const response = await authService.get(
        `admin/get_subjects_for_track/${trackId}/`
      );
      setSubjects(response.data.subjects || []);
    } catch (error) {
      console.error("Error fetching subjects:", error);
      setSubjects([]);
    }
  };

  const fetchContentCreators = async () => {
    try {
      const ccList = await fetchContentCreatorsWithCache();
      setContentCreators(ccList);
    } catch (error) {
      console.error("Error fetching content creators:", error);
      setContentCreators([]);
    }
  };

  const fetchProjects = async (trackId: string, pageNo: number = 1, pageSizeParam: number = 10) => {
    try {
      setLoading(true);
      const response = await authService.get(
        `admin/get-all-project/${trackId}?page=${pageNo}&page_size=${pageSizeParam}`
      );
      setProjects(response.data.projects || []);
      setPage(response.data.page || pageNo);
      setPageSize(response.data.page_size || pageSizeParam);
      setHasNext(response.data.has_next || false);
      setHasPrev(response.data.has_prev || false);
      setTotal(response.data.total || 0);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching projects:", error);
      setProjects([]);
      setHasNext(false);
      setHasPrev(false);
      setTotal(0);
    }
  };

  const handleDelete = (project: Data): void => {
    setSelectedProject(project);
    setShowDeleteModal(true);
  };

  const handleMapClick = (project: Data): void => {
    const projectId = project.project_id || "";
    const projectName = encodeURIComponent(project.project_name || "");
    navigate(
      `/admin/project-batch-mapping/${projectName}?id=${projectId}`
    );
  };

  const confirmDelete = async (): Promise<void> => {
    if (selectedProject) {
      try {
        const json = {
          project_id: selectedProject.project_id,
        };
        await authService.delete("admin/delete-project", json);
        if (selectedTrack) {
          fetchProjects(selectedTrack, page, pageSize);
        }
        setShowDeleteModal(false);
      } catch (error) {
        console.error("Error deleting project:", error);
      }
    }
  };

  return (
    <div
      className="border rounded-2 bg-white my-2 me-2"
      style={{
        height: `calc(100vh - 90px)`,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        style={{ flexShrink: 0 }}
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <FormControl
            variant="outlined"
            size="small"
            style={{ minWidth: 140 }}
          >
            <InputLabel id="project-track-label">Track</InputLabel>
            <Select
              labelId="project-track-label"
              id="project-track-select"
              value={selectedTrack}
              onChange={handleTrackChange}
              label="Track"
              disabled={loading}
              style={{ height: "36px" }}
            >
              <MenuItem value="">Select Track</MenuItem>
              {tracks.map((track) => (
                <MenuItem key={track.track_id} value={track.track_id}>
                  {track.track_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ minWidth: 200 }}
          />
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <button
            className="btn"
            onClick={() => {
              handleModal("new");
            }}
          >
            + New Project
          </button>
          <IconButton
            onClick={handleSettingsClick}
            size="small"
            style={{
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "6px",
              height: "36px",
              width: "36px",
            }}
          >
            <MdSettings size={18} />
          </IconButton>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        className="px-2"
        style={{ flexGrow: 1, overflow: "auto", borderRadius: 0 }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  backgroundColor: "#604262",
                  color: "#fff",
                },
              }}
            >
              {columns
                .filter((col) => col.visible)
                .map((column) => {
                  if (column.key === "SL_No") {
                    return (
                      <TableCell key={column.key} className="muifont mui-header-cell">
                        {column.label}
                      </TableCell>
                    );
                  }
                  if (column.key === "ID") {
                    return (
                      <TableCell
                        key={column.key}
                        className="muifont mui-header-cell mui-sortable-header"
                        onClick={() => handleSort("project_id")}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <span>{column.label}</span>
                          <span style={{ marginLeft: "4px" }}>
                            {sortField === "project_id" ? (
                              sortDirection === "asc" ? (
                                <PiSortDescendingLight size={16} />
                              ) : (
                                <PiSortAscendingLight size={16} />
                              )
                            ) : (
                              <HiOutlineDotsVertical size={16} />
                            )}
                          </span>
                        </Box>
                      </TableCell>
                    );
                  }
                  if (column.key === "project_name") {
                    return (
                      <TableCell
                        key={column.key}
                        className="muifont mui-header-cell mui-sortable-header"
                        onClick={() => handleSort("project_name")}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <span>{column.label}</span>
                          <span style={{ marginLeft: "4px" }}>
                            {sortField === "project_name" ? (
                              sortDirection === "asc" ? (
                                <PiSortDescendingLight size={16} />
                              ) : (
                                <PiSortAscendingLight size={16} />
                              )
                            ) : (
                              <HiOutlineDotsVertical size={16} />
                            )}
                          </span>
                        </Box>
                      </TableCell>
                    );
                  }
                  if (column.key === "project_description") {
                    return (
                      <TableCell
                        key={column.key}
                        className="muifont mui-header-cell mui-sortable-header"
                        onClick={() => handleSort("project_description")}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <span>{column.label}</span>
                          <span style={{ marginLeft: "4px" }}>
                            {sortField === "project_description" ? (
                              sortDirection === "asc" ? (
                                <PiSortDescendingLight size={16} />
                              ) : (
                                <PiSortAscendingLight size={16} />
                              )
                            ) : (
                              <HiOutlineDotsVertical size={16} />
                            )}
                          </span>
                        </Box>
                      </TableCell>
                    );
                  }
                  if (column.key === "company_name") {
                    return (
                      <TableCell
                        key={column.key}
                        className="muifont mui-header-cell mui-sortable-header"
                        onClick={() => handleSort("company_name")}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <span>{column.label}</span>
                          <span style={{ marginLeft: "4px" }}>
                            {sortField === "company_name" ? (
                              sortDirection === "asc" ? (
                                <PiSortDescendingLight size={16} />
                              ) : (
                                <PiSortAscendingLight size={16} />
                              )
                            ) : (
                              <HiOutlineDotsVertical size={16} />
                            )}
                          </span>
                        </Box>
                      </TableCell>
                    );
                  }
                  if (column.key === "industry") {
                    return (
                      <TableCell
                        key={column.key}
                        className="muifont mui-header-cell mui-sortable-header"
                        onClick={() => handleSort("industry")}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <span>{column.label}</span>
                          <span style={{ marginLeft: "4px" }}>
                            {sortField === "industry" ? (
                              sortDirection === "asc" ? (
                                <PiSortDescendingLight size={16} />
                              ) : (
                                <PiSortAscendingLight size={16} />
                              )
                            ) : (
                              <HiOutlineDotsVertical size={16} />
                            )}
                          </span>
                        </Box>
                      </TableCell>
                    );
                  }
                  if (column.key === "status") {
                    return (
                      <TableCell
                        key={column.key}
                        className="muifont mui-header-cell mui-sortable-header"
                        onClick={() => handleSort("status")}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <span>{column.label}</span>
                          <span style={{ marginLeft: "4px" }}>
                            {sortField === "status" ? (
                              sortDirection === "asc" ? (
                                <PiSortDescendingLight size={16} />
                              ) : (
                                <PiSortAscendingLight size={16} />
                              )
                            ) : (
                              <HiOutlineDotsVertical size={16} />
                            )}
                          </span>
                        </Box>
                      </TableCell>
                    );
                  }
                  if (column.key === "batch") {
                    return (
                      <TableCell
                        key={column.key}
                        className="muifont mui-header-cell mui-sortable-header"
                        onClick={() => handleSort("batch")}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <span>{column.label}</span>
                          <span style={{ marginLeft: "4px" }}>
                            {sortField === "batch" ? (
                              sortDirection === "asc" ? (
                                <PiSortDescendingLight size={16} />
                              ) : (
                                <PiSortAscendingLight size={16} />
                              )
                            ) : (
                              <HiOutlineDotsVertical size={16} />
                            )}
                          </span>
                        </Box>
                      </TableCell>
                    );
                  }
                  if (column.key === "actions") {
                    return (
                      <TableCell
                        key={column.key}
                        className="muifont mui-header-cell"
                        style={{
                          borderRight: "none",
                        }}
                      >
                        {column.label}
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell key={column.key} className="muifont mui-header-cell">
                      {column.label}
                    </TableCell>
                  );
                })}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedProjects.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.filter((col) => col.visible).length}
                  align="center"
                  style={{ padding: "20px", color: "#666" }}
                >
                  No projects found
                </TableCell>
              </TableRow>
            ) : (
              sortedProjects.map((project, index) => (
                <TableRow
                  key={index}
                  style={{
                    backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                  }}
                  sx={{
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                >
                  {columns
                    .filter((col) => col.visible)
                    .map((column) => {
                      if (column.key === "SL_No") {
                        return (
                          <TableCell key={column.key} className="muifont mui-body-cell">
                            {index + 1}
                          </TableCell>
                        );
                      }
                      if (column.key === "ID") {
                        return (
                          <TableCell key={column.key} className="muifont mui-body-cell">
                            {project.project_id || ""}
                          </TableCell>
                        );
                      }
                      if (column.key === "project_name") {
                        return (
                          <TableCell
                            key={column.key}
                            className="muifont mui-body-cell text-decoration-underline"
                            title="Configure Project"
                            role="button"
                            onClick={() => {
                              navigate(`/admin/project-configuration`, {
                                state: {
                                  track_id: project.track_id,
                                  project_id: project.project_id,
                                  project_name: project.project_name,
                                },
                              });
                            }}
                          >
                            {project.project_name || ""}
                          </TableCell>
                        );
                      }
                      if (column.key === "project_description") {
                        const description = project.project_description || "";
                        const truncatedDesc = description.length > 10 
                          ? `${description.substring(0, 10)}...` 
                          : description;
                        return (
                          <TableCell 
                            key={column.key} 
                            className="muifont mui-body-cell"
                            title={description}
                          >
                            {truncatedDesc}
                          </TableCell>
                        );
                      }
                      if (column.key === "company_name") {
                        return (
                          <TableCell key={column.key} className="muifont mui-body-cell">
                            {project.company_name || ""}
                          </TableCell>
                        );
                      }
                      if (column.key === "industry") {
                        return (
                          <TableCell key={column.key} className="muifont mui-body-cell">
                            {project.industry || ""}
                          </TableCell>
                        );
                      }
                      if (column.key === "status") {
                        const statusColor = PROJECT_STATUS_COLORS[project.status] || "#000";
                        return (
                          <TableCell key={column.key} className="muifont mui-body-cell">
                            <span style={{ color: statusColor}}>
                              {project.status}
                            </span>
                          </TableCell>
                        );
                      }
                      if (column.key === "batch") {
                        return (
                          <TableCell key={column.key} className="muifont mui-body-cell">
                            {project.batches?.length || 0}
                          </TableCell>
                        );
                      }
                      if (column.key === "actions") {
                        return (
                          <TableCell
                            key={column.key}
                            className="muifont mui-body-cell"
                            style={{
                              borderRight: "none",
                              minWidth: "150px",
                            }}
                          >
                            <FaEdit
                              role="button"
                              title="Edit"
                              onClick={() => {
                                handleModal("edit", project);
                              }}
                              className="fs-5 me-2"
                            />
                            <FaTrash
                              title={
                                project.status === "Published"
                                  ? "Cannot delete published project"
                                  : "Delete"
                              }
                              onClick={
                                project.status === "Published"
                                  ? undefined
                                  : () => handleDelete(project)
                              }
                              className="fs-5 me-2"
                              style={{
                                cursor: project.status === "Published" ? "not-allowed" : "pointer",
                                opacity: project.status === "Published" ? 0.5 : 1,
                                pointerEvents: project.status === "Published" ? "none" : "auto",
                              }}
                            />

                            <button
                              type="button"
                              title="Batch Map"
                              disabled={project.status !== "Published"}
                              onClick={() => handleMapClick(project)}
                              style={{
                                background: "none",
                                border: "none",
                                padding: 0,
                                cursor:
                                  project.status !== "Published" ? "not-allowed" : "pointer",
                                marginRight: "8px",
                              }}
                            >
                              <FaLink />
                            </button>
                              <FaUsers className="fs-5"
                              style={{
                                cursor: project.status !== "Published" ? "not-allowed" : "pointer",
                                color: project.status !== "Published" ? "#666" : "#000",
                              }}
                              onClick={() => {
                                if (project.status !== "Published") {
                                  return;
                                }
                                const projectName = encodeURIComponent(project.project_name || "");
                                navigate(
                                  `/admin/project-slot-listing/${projectName}?id=${project.project_id}`,
                                  {
                                    state: {
                                      track_id: project.track_id,
                                    },
                                  }
                                );
                              }}
                              title="View Slots"
                              role="button"
                              />
                          </TableCell>
                        );
                      }
                      return null;
                    })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        style={{ flexShrink: 0, borderTop: "1px solid #e0e0e0" }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {projects.length > 0 ? ((page - 1) * pageSize + 1) : 0} to{" "}
          {Math.min(page * pageSize, total)} of {total} projects
        </Typography>
        <Box display="flex" alignItems="center">
          <FaChevronLeft
            onClick={() => {
              if (hasPrev && page > 1) {
                const newPage = page - 1;
                setPage(newPage);
                if (selectedTrack) {
                  fetchProjects(selectedTrack, newPage, pageSize);
                }
              }
            }}
            style={{
              cursor: hasPrev && page > 1 && !loading ? "pointer" : "not-allowed",
              opacity: hasPrev && page > 1 && !loading ? 1 : 0.5,
            }}
          />

          <Typography className="mx-2" variant="body2">
            Page {page}
          </Typography>

            <FaChevronRight 
              onClick={() => {
                if (hasNext) {
                  const newPage = page + 1;
                  setPage(newPage);
                  fetchProjects(selectedTrack, newPage, pageSize);
                }
              }}
              style={{
                cursor: hasNext && !loading ? "pointer" : "not-allowed",
                opacity: hasNext && !loading ? 1 : 0.5,
              }}
            />

        </Box>
      </Box>

      {/* Column Settings Popover */}
      <Popover
        open={Boolean(settingsAnchor)}
        anchorEl={settingsAnchor}
        onClose={handleSettingsClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Column Visibility
            </Typography>
            <MUIButton
              size="small"
              variant="outlined"
              onClick={() => {
                const resetColumns = resetColumnVisibility(PAGE_KEYS.PROJECT, getDefaultProjectColumns());
                setColumns(resetColumns);
              }}
              sx={{ fontSize: '12px', py: 0.5 }}
            >
              Reset
            </MUIButton>
          </Box>
          {columns.map((column) => (
            <FormControlLabel
              key={column.key}
              control={
                <Checkbox
                  checked={column.visible}
                  onChange={() => handleColumnVisibilityChange(column.key)}
                  disabled={column.key === "actions"} // Always keep actions column visible
                />
              }
              label={column.label}
              sx={{ display: "block", mb: 1 }}
            />
          ))}
        </Box>
      </Popover>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalType === "new" ? "Add New Project" : "Edit Project"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {error && <p style={{ color: "red" }}>{error}</p>}

            {/* Row 1: Track and Subject */}
            <div className="row mt-3">
              <div className="col-md-6">
                <Form.Group controlId="formTrackId">
                  <Form.Label>Track</Form.Label>
                  <Form.Select
                    name="track_id"
                    value={formData.track_id}
                    disabled={formData.project_id != ""}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        track_id: e.target.value,
                        project_allowed_subjects: [],
                      });
                    }}
                    style={{ height: "36px" }}
                  >
                    <option value="">Select</option>
                    {tracks.map((track) => (
                      <option key={track.track_id} value={track.track_id}>
                        {track.track_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group controlId="formProjectSubjects">
                  <Form.Label>Subjects</Form.Label>
                  <MultiSelect
                    options={subjects.map(
                      (subject): OptionItem => {
                        // Check if subject is used in project_data
                        const subjectsInProjectData = formData.project_data 
                          ? getSubjectsFromProjectData(formData.project_data)
                          : new Set<string>();
                        const isSubjectUsed = subjectsInProjectData.has(subject.subject_id);
                        
                        return {
                          value: subject.subject_id,
                          label: subject.is_project
                            ? `${subject.subject_name} (Project)`
                            : subject.subject_name,
                          className: subject.is_project ? "bg-light" : "",
                          style: subject.is_project
                            ? { backgroundColor: "#e3f2fd" }
                            : undefined,
                          disabled: isSubjectUsed, // Disable if subject is used in project_data
                        };
                      }
                    )}
                    value={formData.project_allowed_subjects || []}
                    onChange={handleSubjectChange}
                    placeholder={
                      subjects.length === 0
                        ? "Please select a track first"
                        : "Select subjects..."
                    }
                    disabled={subjects.length === 0}
                    maxMenuHeight={200}
                  />
                </Form.Group>
              </div>
            </div>

            {/* Row 2: Company and Domain */}
            <div className="row mt-3">
              <div className="col-md-6">
                <Form.Group controlId="formCompanyId">
                  <Form.Label>Company</Form.Label>
                  <Form.Select
                    name="company_id"
                    value={formData.company_id}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        company_id: e.target.value,
                        industry: "",
                      });
                    }}
                    disabled={formData.status === "Published"}
                    style={{ height: "36px" }}
                  >
                    <option value="">Select</option>
                    {companies
                      .filter((company) => !isCompanyDeleted(company))
                      .map((company) => (
                        <option
                          key={company.company_id}
                          value={company.company_id}
                        >
                          {company.company_name}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group controlId="formIndustry">
                  <Form.Label>Domain (Industry)</Form.Label>
                  <Form.Select
                    name="industry"
                    value={formData.industry}
                    onChange={(e) => {
                      setFormData({ ...formData, industry: e.target.value });
                    }}
                    disabled={formData.status === "Published"}
                    style={{ height: "36px" }}
                  >
                    <option value="">Select</option>
                    {industries.map((industry, index) => (
                      <option key={index} value={industry.industry_name}>
                        {industry.industry_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            {/* Row 3: Level and Content Creators */}
            <div className="row mt-3">
              <div className="col-md-6">
                <Form.Group controlId="formLevel">
                  <Form.Label>Level</Form.Label>
                  <Form.Select
                    name="level"
                    value={formData.level}
                    onChange={(e) => {
                      setFormData({ ...formData, level: e.target.value });
                    }}
                    disabled={formData.status === "Published"}
                    style={{ height: "36px" }}
                  >
                    <option value="">Select</option>
                    <option value="level1">Level 1</option>
                    <option value="level2">Level 2</option>
                    <option value="level3">Level 3</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group controlId="formContentCreators">
                  <Form.Label>Content Creators</Form.Label>
                  <MultiSelect
                    options={contentCreators.map(
                      (cc): OptionItem => ({
                        value: cc.user_id,
                        label: cc.user_name,
                      })
                    )}
                    value={selectedUserIds}
                    onChange={setSelectedUserIds}
                    placeholder={
                      contentCreators.length === 0
                        ? "No content creators available"
                        : "Select content creators..."
                    }
                    disabled={contentCreators.length === 0}
                    maxMenuHeight={200}
                  />
                </Form.Group>
              </div>
            </div>

            {/* Row 4: Project Name */}
            <div className="row mt-3">
              <div className="col-12">
                <Form.Group controlId="formProjectName">
                  <Form.Label>Project Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="project_name"
                    value={formData.project_name}
                    onChange={handleChange}
                    placeholder="Enter Project Name"
                  />
                </Form.Group>
              </div>
            </div>

            {/* Row 5: Project Description (Full Width) */}
            <div className="row mt-3">
              <div className="col-12">
                <Form.Group controlId="formProjectDescription">
                  <Form.Label>Project Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="project_description"
                    value={formData.project_description || ""}
                    onChange={handleChange}
                    placeholder="Enter Project Description"
                  />
                </Form.Group>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {loading ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete the project:{" "}
            <strong>{selectedProject?.project_name}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            No
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Yes, Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Project;
